"use client";

import { endOfDay, startOfDay } from "date-fns";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";
import * as React from "react";
import { DayPicker, DayPickerSingleProps, SelectSingleEventHandler } from "react-day-picker";

import { TimePickerInput } from "@/components/TimePicker/TimePickerInput";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/utils";

export type DatetimePickerProps = Omit<DayPickerSingleProps, "mode" | "onSelect"> & {
    setDate: (date: Date) => void;
};

function DatetimePicker({
    className,
    classNames,
    showOutsideDays = true,
    setDate: setGlobalDate,
    ...props
}: DatetimePickerProps) {
    const minuteRef = React.useRef<HTMLInputElement>(null);
    const hourRef = React.useRef<HTMLInputElement>(null);
    const secondRef = React.useRef<HTMLInputElement>(null);
    const millisecondRef = React.useRef<HTMLInputElement>(null);
    const { selected: selectedDate } = props as { selected: Date };
    const setDate = (dateInput: Date) => {
        const date = new Date(selectedDate);
        date.setDate(dateInput.getDate());
        date.setMonth(dateInput.getMonth());
        date.setFullYear(dateInput.getFullYear());
        setGlobalDate(date);
    };
    const setTime = (dateInput: Date | undefined) => {
        if (!dateInput) return;
        const time = new Date(selectedDate);
        time.setHours(dateInput.getHours());
        time.setMinutes(dateInput.getMinutes());
        time.setSeconds(dateInput.getSeconds());
        time.setMilliseconds(dateInput.getMilliseconds());
        setGlobalDate(time);
    };
    return (
        <div className="flex flex-col items-center">
            <DayPicker
                mode="single"
                selected={selectedDate}
                onSelect={setDate as SelectSingleEventHandler}
                showOutsideDays={showOutsideDays}
                className={cn("py-3", className)}
                classNames={{
                    months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                    month: "space-y-4",
                    caption: "flex justify-center pt-1 relative items-center",
                    caption_label: "text-sm font-medium",
                    nav: "space-x-1 flex items-center",
                    nav_button: cn(
                        buttonVariants({ variant: "outline" }),
                        "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
                    ),
                    nav_button_previous: "absolute left-1",
                    nav_button_next: "absolute right-1",
                    table: "w-full border-collapse space-y-1",
                    head_row: "flex",
                    head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
                    row: "flex w-full",
                    cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                    day: cn(
                        buttonVariants({ variant: "ghost" }),
                        "h-9 w-9 p-0 font-normal aria-selected:opacity-100"
                    ),
                    day_range_end: "day-range-end",
                    day_selected:
                        "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                    day_today: "bg-accent text-accent-foreground",
                    day_outside:
                        "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
                    day_disabled: "text-muted-foreground opacity-50",
                    day_range_middle:
                        "aria-selected:bg-accent aria-selected:text-accent-foreground",
                    day_hidden: "invisible",
                    ...classNames,
                }}
                components={{
                    IconLeft: _ => <ChevronLeft className="h-4 w-4" />,
                    IconRight: _ => <ChevronRight className="h-4 w-4" />,
                }}
                {...props}
            />
            <hr className="my-0" />
            <div className="flex flex-col gap-2">
                <div className="mt-4 flex justify-between gap-2 px-2">
                    <div className="flex items-center gap-2 text-gray-700">
                        <Clock className="h-5 w-5" />
                        <p className="text-sm font-medium">Time</p>
                    </div>
                    <div className="font-medium">
                        <div className="flex items-center gap-2">
                            <TimePickerInput
                                picker="hours"
                                date={selectedDate}
                                setDate={setTime}
                                ref={hourRef}
                                onRightFocus={() => minuteRef.current?.focus()}
                                onLeftFocus={() => millisecondRef.current?.focus()}
                            />
                            <span>:</span>
                            <TimePickerInput
                                picker="minutes"
                                date={selectedDate}
                                setDate={setTime}
                                ref={minuteRef}
                                onRightFocus={() => secondRef.current?.focus()}
                                onLeftFocus={() => hourRef.current?.focus()}
                            />
                            <span>:</span>
                            <TimePickerInput
                                picker="seconds"
                                date={selectedDate}
                                setDate={setTime}
                                ref={secondRef}
                                onRightFocus={() => millisecondRef.current?.focus()}
                                onLeftFocus={() => minuteRef.current?.focus()}
                            />
                            <span>.</span>
                            <TimePickerInput
                                picker="milliseconds"
                                date={selectedDate}
                                setDate={setTime}
                                ref={millisecondRef}
                                onLeftFocus={() => secondRef.current?.focus()}
                                onRightFocus={() => hourRef.current?.focus()}
                            />
                        </div>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        className="text-gray-700"
                        onClick={() => {
                            setTime(startOfDay(new Date()));
                        }}
                    >
                        Start of day
                    </Button>
                    <Button
                        variant="outline"
                        className="text-gray-700"
                        onClick={() => {
                            setTime(endOfDay(new Date()));
                        }}
                    >
                        End of day
                    </Button>
                </div>
            </div>
        </div>
    );
}

DatetimePicker.displayName = "DatetimePicker";

export { DatetimePicker as DatetimePicker };
